// @ts-check
/**
 * **HUD の決まり**（Issue #523。`duelEditor/core/` ＝ 舞台の決まりごと）。契約は `types.d.ts`。
 *
 * HUD は盤の外の表示なので、盤（平面の紙）とは違って**舞台のピクセル**で考える。
 * ここが持つのは「HUD とは何か」だけ —— **どこに何があるかは実装（テーマ）の自由**。
 *
 * ## 外向けの関数は「名前」で答える
 *
 * 旧 `/fx` は 2026-09-23 に HUD の作り替えを 3 回（#507 / #508 / #509）やって **3 回とも revert** した。
 * 毎回壊れたのは**演出が HUD の内部の箱（`layout.hud.plates[0]`）を直読みしていた**ところで、
 * 契約が「名前」ではなく「箱の形」だったから。**ここが公開するのは名前だけ**（`ANCHOR_PORT`）。
 *
 * ## 規約
 *
 * - 返すのは**舞台のピクセルの箱** `{ x, y, w, h }`
 * - **その HUD に無い部品は `null`**（呼ぶ側が fallback を持つ）。ただし **`life` / `hand` は
 *   必ず箱を返す** —— **fallback はここが持つ**ので、実装が答えなくても呼ぶ側は箱を得る
 * - **フレームごとに変わる値は引数で受け取る**（`turnBadge(値)`）。プロパティにすると場所が
 *   毎フレーム作り直され、あとで繋ぐ演出のインスタンスまで作り直しになる
 * - ここは純関数だけ（`purity.test.js`）
 */

import { applyToBox, isIdentity, transformOf } from '../transform';

/** @typedef {import('./types').Hud} Hud */
/** @typedef {import('./types').HudAnchors} HudAnchors */
/** @typedef {import('./types').HudValues} HudValues */

/* ------------------------------------------------------------------ *
 *  文字の大きさの下限（全テーマ共通の決まり）
 * ------------------------------------------------------------------ */

/**
 * **スマホで完成した動画を見る人が読める下限**（舞台 1920×1080 のピクセル）。
 * 下限のぶん箱が足りなくなるものは、文字ではなく**箱の側を伸ばす**。
 */
export const MIN_FONT = 46;

/**
 * @param {number} size 本来の大きさ（箱の高さなどから決めた値）
 * @param {number} [smallestEm] その中の一番小さい子の em 比（0.72em の子があるなら 0.72）
 * @returns {number}
 */
export function fontPx(size, smallestEm = 1) {
  const em = typeof smallestEm === 'number' && Number.isFinite(smallestEm) && smallestEm > 0 ? smallestEm : 1;
  const v = typeof size === 'number' && Number.isFinite(size) ? size : 0;
  return Math.round(Math.max(MIN_FONT / em, v));
}

/* ------------------------------------------------------------------ *
 *  値（そのフレームの値）
 * ------------------------------------------------------------------ */

export const DEFAULT_LIFE = 8000;

/** プレイヤーの色の既定（`utils/gameInfo.js::DEFAULT_PLAYER_COLORS` と同じ青 / 赤） */
export const PLAYER_COLORS = Object.freeze(['#3fa9f5', '#f5563f']);

const num = (v, alt) => (typeof v === 'number' && Number.isFinite(v) ? v : alt);

/**
 * HUD が読む値を穴埋めする。**どのテーマも同じ形を受け取る**ので、値の有無で落ちない。
 *
 * **LP の動き（イージング・残像）は持たない** —— 舞台は時計を持たない（`purity.test.js`）ので、
 * 動かしたければ呼ぶ側が `lifeGhost` に値を入れる。
 *
 * @param {object|null} raw
 * @returns {HudValues}
 */
export function hudValues(raw) {
  const players = [0, 1].map((i) => {
    const p = raw?.players?.[i] ?? {};
    return Object.freeze({ ...p, color: p.color || PLAYER_COLORS[i] });
  });
  const life = [0, 1].map((i) => num(raw?.life?.[i], DEFAULT_LIFE));
  return Object.freeze({
    players: Object.freeze(players),
    life: Object.freeze(life),
    lifeGhost: Object.freeze([0, 1].map((i) => num(raw?.lifeGhost?.[i], life[i]))),
    lifeTarget: Object.freeze([0, 1].map((i) => num(raw?.lifeTarget?.[i], life[i]))),
    lifeFrom: Object.freeze([0, 1].map((i) => num(raw?.lifeFrom?.[i], num(raw?.lifeTarget?.[i], life[i])))),
    hands: Object.freeze([0, 1].map((i) => num(raw?.hands?.[i], 0))),
    turn: num(raw?.turn, 0),
    phaseLabel: raw?.phaseLabel ?? null,
    turnPlayer: raw?.turnPlayer === 1 ? 1 : 0,
  });
}

/* ------------------------------------------------------------------ *
 *  場所（名前で答える）
 * ------------------------------------------------------------------ */

/** **外向けの関数の名前**（`types.d.ts` の `HudAnchors` と 1 対 1） */
export const ANCHOR_PORT = Object.freeze(['plate', 'life', 'hand', 'turnBadge']);

/**
 * **口の名前 → 人に見せる見出し**（Issue #526）。
 * 部品の見出しは**宣言から引く**（画面側に手書きの表を置かない）ので、`ANCHOR_PORT` を足したら
 * ここにも 1 行足す（`core/theme::STAGE_PARTS` が使う）。
 */
export const ANCHOR_LABELS = Object.freeze({
  plate: '枠',
  life: 'ライフ',
  hand: '手札',
  turnBadge: 'ターン',
});

/**
 * **対称の置き方**（Issue #523。ユーザー要望 2026-09-24）。
 *
 * HUD は「**P1 の箱を作って、P2 はその反対側に置く**」作りになっている。その「反対側」を**値にする**と、
 * 「左右対称のスライダー」と「1 つずつ自由に置く」が**選べるもの**になって、分ける必要がなくなる。
 *
 * | | P2 の置き方 |
 * |---|---|
 * | `'mirror'`（既定） | **左右対称**（今までの挙動） |
 * | `'point'` | **点対称**（舞台の中心まわりに 180°） |
 * | `'free'` | **そのまま**（テーマが P2 用の箱を別に持つ） |
 *
 * どのテーマでも同じ意味なので core が持つ（`sideOf` / `PLAYER_COLORS` と同じ並び）。
 */
export const SYMMETRY_MODES = Object.freeze(['mirror', 'point', 'free']);

export function mirrorBox(box, canvas, mode = 'mirror') {
  if (!box || mode === 'free') return box;
  const x = canvas.w - box.x - box.w;
  return mode === 'point' ? { ...box, x, y: canvas.h - box.y - box.h } : { ...box, x };
}

/* ------------------------------------------------------------------ *
 *  トランスフォーム（全部品に付く共通のパラメータ）
 * ------------------------------------------------------------------ */

/**
 * **箱 → CSS**（Issue #523）。`applyToBox` を通した箱をそのまま DOM に置く形にする。
 *
 * **大きさは `w` / `h` に入っている**（`applyToBox` が掛けたもの）ので、ここで `scale()` は当てない
 * —— 掛けると二重になる。回転だけは箱では表せないので `transform` に出し、**基準点はアンカー**。
 */
export function partStyle(box) {
  if (!box) return null;
  const out = { left: box.x, top: box.y, width: box.w, height: box.h };
  if (box.rotation) {
    out.transform = `rotate(${box.rotation}deg)`;
    out.transformOrigin = `${(box.anchorX ?? 0.5) * 100}% ${(box.anchorY ?? 0.5) * 100}%`;
  }
  if (Number.isFinite(box.opacity) && box.opacity !== 1) out.opacity = box.opacity;
  return out;
}

/**
 * **箱を持たない要素に当てる**（Issue #523）。枠の中の LP の数字のように、
 * テーマが自前で置いている要素は箱が `anchors` に出ていないので、**CSS の transform だけ**を重ねる。
 * 素のままなら `null`（`style` に何も足さない ＝ 今までと 1px も変わらない）。
 */
export function partTransform(tf, base = null) {
  if (!tf || isIdentity(tf)) return null;
  // **要素が自分で持っている transform を先に置く**（CSS の `skewX` などを消さないため）
  const parts = base ? [base] : [];
  if (tf.x || tf.y) parts.push(`translate(${tf.x}px, ${tf.y}px)`);
  if (tf.rotation) parts.push(`rotate(${tf.rotation}deg)`);
  if (tf.scale !== 1) parts.push(`scale(${tf.scale})`);
  const out = {};
  if (parts.length) {
    out.transform = parts.join(' ');
    out.transformOrigin = `${tf.anchorX * 100}% ${tf.anchorY * 100}%`;
  }
  if (tf.opacity !== 1) out.opacity = tf.opacity;
  return Object.keys(out).length ? out : null;
}

/** テーマが枠の中の要素に当てるための近道（`partTransform(transformOf(style, part))`） */
export const partCss = (style, part, base = null) => partTransform(transformOf(style, part), base);

/** 契約の口の一覧（`types.d.ts` の `Hud` と 1 対 1） */
export const HUD_PORT = Object.freeze(['anchors', 'Component', 'options']);

/**
 * **演出が HUD に当てる作用**（Issue #542。索引 #539）。演出の `hud(ctx)` が返すもの:
 *
 * ```js
 * [{ anchor: 'life', player: 1, shake: 0.8, flash: 1, color: '#ff2e3c' }]
 * ```
 *
 * | 鍵 | 範囲 | 意味 |
 * |---|---|---|
 * | `shake` | 0〜1 | 揺れの強さ（揺らし方はテーマ） |
 * | `flash` | 0〜1 | 光の強さ（色は `color`、無ければテーマの既定） |
 * | `pulse` | 0〜1 | 脈打つ・膨らむ強さ |
 *
 * **HUD は時計を持たない**ので、揺れの位相は `phase`（帯の経過 ms）で渡す —— テーマが
 * `sin(phase)` などで決めれば、同じフレームなら同じ絵になる。
 *
 * 同じ部品に複数の帯が当てたら**強いほう**（`max`）。
 */
export const HUD_FX_KEYS = Object.freeze(['shake', 'flash', 'pulse']);

/**
 * 部品の鍵（`life:0` / `turnBadge`）
 * @param {string} anchor
 * @param {number|null} [player]
 */
export const hudFxKey = (anchor, player = null) => (anchor === 'turnBadge' || player == null ? String(anchor) : `${anchor}:${player === 1 ? 1 : 0}`);

/**
 * 演出の作用の一覧 → **部品ごとの作用**（純関数）。知らない部品は捨てる。
 * `life`（`anchor: 'life'` だけ）は 0〜1 ではなく**その間 HUD に見せる LP の値**（Issue #554。ダメージの数字が着くまで減る前の LP を見せる）。
 * 読まない HUD はそのまま本来の値を出す。
 * `appear`（0〜1）は**部品の登場の進み**（Issue #646。0 = まだ出ていない / 1 = 定位置）。ほかの作用と逆に**小さいほうが勝つ**
 * （隠したい演出がいれば隠す）。誰も言わなければ 1（出たまま）。読まない HUD（arcade）は今までどおり出たまま。
 * @param {Array<{anchor: string, player?: number, shake?: number, flash?: number, pulse?: number, color?: string, phase?: number, life?: number, appear?: number}>} list
 */
export function foldHudFx(list = []) {
  /** @type {Record<string, any>} */
  const out = {};
  (list ?? []).forEach((e) => {
    if (!e || !ANCHOR_PORT.includes(e.anchor)) return;
    const key = hudFxKey(e.anchor, e.anchor === 'turnBadge' ? null : (e.player ?? 0));
    const cur = out[key] ?? { shake: 0, flash: 0, pulse: 0, color: null, phase: 0, life: null, appear: 1 };
    HUD_FX_KEYS.forEach((k) => {
      const v = Number(e[k]);
      if (Number.isFinite(v) && v > cur[k]) {
        cur[k] = Math.min(1, Math.max(0, v));
        if (k === 'flash' && e.color) cur.color = e.color;
      }
    });
    if (!cur.color && e.color) cur.color = e.color;
    if (Number.isFinite(e.phase)) cur.phase = e.phase;
    // 見せる LP（値。0〜1 ではない）。複数あれば後のもの
    if (e.anchor === 'life' && typeof e.life === 'number' && Number.isFinite(e.life)) cur.life = Math.max(0, e.life);
    // 登場の進み（小さいほうが勝つ。0 で隠す）
    const ap = Number(e.appear);
    if (e.appear != null && Number.isFinite(ap)) cur.appear = Math.min(cur.appear, Math.min(1, Math.max(0, ap)));
    out[key] = cur;
  });
  return Object.freeze(out);
}

/**
 * 部品の作用（無ければ 0 の作用）。テーマの HUD が読む
 * @param {any} fx
 * @param {string} anchor
 * @param {number|null} [player]
 */
export const hudFxOf = (fx, anchor, player = null) => fx?.[hudFxKey(anchor, player)] ?? NO_FX;
const NO_FX = Object.freeze({ shake: 0, flash: 0, pulse: 0, color: null, phase: 0, life: null, appear: 1 });

/** 枠を持たない HUD 向けの LP の場所（舞台の上の左右） */
const fallbackLife = (canvas, p) => ({ x: p === 1 ? canvas.w * 0.8 : canvas.w * 0.2, y: canvas.h * 0.1, w: 400, h: 120 });

/** 枠を持たない HUD 向けの手札の場所（舞台の下の左右。x, y は中心） */
const fallbackHand = (canvas, p) => ({ x: p === 1 ? canvas.w * 0.85 : canvas.w * 0.15, y: canvas.h * 0.9, w: 300, h: 110 });

const idx = (player) => (player === 1 ? 1 : 0);

/**
 * 実装が答えた場所に**欠けた口と fallback を埋める**。
 * @param {object} raw 実装の `anchors(ctx)` の結果
 * @param {{w: number, h: number}} canvas 舞台の大きさ
 * @returns {HudAnchors}
 */
export function normalizeAnchors(raw, canvas, style = null) {
  const got = raw ?? {};
  /*
   * **トランスフォームはここで当てる**（Issue #523）。`anchors` が場所の契約なので、
   * ここで当てておけば**読む側（HUD の絵・編集の層・あとで繋ぐ演出）が全部付いてくる**。
   * 中心で持つ口（`hand` は x, y が中心、`turnBadge` は x が中心）は左上に直してから当てる。
   */
  const tf = (name) => transformOf(style, `hud:${name}`);
  const box = (name, b) => (b ? applyToBox(b, tf(name)) : b);
  const fromCenter = (name, b, cx = true, cy = true) => {
    if (!b) return b;
    const out = applyToBox({ ...b, x: cx ? b.x - b.w / 2 : b.x, y: cy ? b.y - b.h / 2 : b.y }, tf(name));
    return { ...out, x: cx ? out.x + out.w / 2 : out.x, y: cy ? out.y + out.h / 2 : out.y };
  };
  return Object.freeze({
    ...got,
    plate: (player) => box('plate', got.plate?.(idx(player)) ?? null),
    // **必ず箱を返す**（実装が答えなければ舞台基準）
    life: (player) => box('life', got.life?.(idx(player)) ?? fallbackLife(canvas, idx(player))),
    hand: (player) => fromCenter('hand', got.hand?.(idx(player)) ?? fallbackHand(canvas, idx(player))),
    turnBadge: (values) => fromCenter('turnBadge', got.turnBadge?.(values) ?? null, true, false),
  });
}

/**
 * HUD を作る。**どのテーマの HUD もここを通る**ので口が欠けない。
 *
 * @param {object} spec
 * @param {Function} spec.anchors `(ctx) => { plate, life, hand, turnBadge }`。ctx = `{ canvas, view, options }`
 * @param {Function} spec.Component 絵（DOM）。`({ values, anchors, options }) => JSX`
 * @param {object} [spec.options] その HUD の設定の既定
 * @returns {Hud}
 */
export function toHud({ anchors, Component, options = {} }) {
  if (typeof anchors !== 'function') throw new Error('hud: anchors は関数です');
  if (typeof Component !== 'function') throw new Error('hud: Component は関数です');
  if (typeof options !== 'object' || options === null) throw new Error('hud: options は object です');
  return Object.freeze({
    /** 場所（欠けた口と fallback を埋めて返す） */
    anchors: (ctx) => normalizeAnchors(anchors(ctx), ctx?.canvas ?? { w: 1920, h: 1080 }, ctx?.style),
    Component,
    options: Object.freeze({ ...options }),
  });
}
